/**
 * Tile Configuration for the Three-Pane Layout System
 * 
 * The deck builder uses a three-pane layout:
 *   Left (Deck) | Middle (Search) | Right (Preview + Utilities, fixed width)
 * 
 * The deck and search panes share remaining space as fr units.
 * The right pane has a fixed pixel width.
 * 
 * View mode controls the deck/search ratio:
 *   large   → 50/50   (1:1)
 *   medium  → 30/20   (3:2)
 *   details → 30/15   (2:1)
 */

// Grid configuration
export const GRID_CONFIG = {
  // Width of the right pane (preview + utility tiles) in pixels.
  // At narrow desktop widths (1100-1300px) this scales down; see getRightPaneWidth().
  rightPaneWidth: 320,
  rightPaneMinWidth: 240,

  // Deck / Search flex ratios keyed by cardViewMode
  viewModeRatios: {
    large:   { deck: 1, search: 1 },   // 50-50
    medium:  { deck: 3, search: 2 },   // 30-20 ratio
    details: { deck: 2, search: 1 },   // 30-15 ratio (list view)
  },

  // Legacy — kept for backward compat; not used by the new TileGrid
  totalColumns: 10,
  layouts: {
    default: { deckArea: 7, tileArea: 3 },
    expanded: { deckArea: 4, tileArea: 6 },
  },
  heightUnit: 60,
  gap: 12,
};

// Tile definitions with their configurations
export const TILE_CONFIG = {
  deckStats: {
    id: 'deckStats',
    icon: '📊',
    titleKey: 'sidebar.stats.title',
    defaultTitle: 'Deck Stats',
    defaultHeight: 'auto',  // Auto-expand to fit content
    minHeight: 1,           // Minimum height (collapsed state)
    maxHeight: 12,          // Allow larger expansion for charts
    defaultCollapsed: false, // Start expanded
    order: 1,               // Display order in the tile grid
    // Deck stats can expand to fill available space when not collapsed
    canFill: true,
  },
  cardSearch: {
    id: 'cardSearch',
    icon: '🔍',
    titleKey: 'sidebar.search.title',
    defaultTitle: 'Card Search',
    defaultHeight: 'auto', // Auto-fill available space
    minHeight: 1, // Collapsed height should be minimal
    maxHeight: 12,
    defaultCollapsed: false,
    order: 2,
    // Card search is the primary tile - takes remaining space
    isPrimary: true,
  },
  recommendations: {
    id: 'recommendations',
    icon: '💡',
    titleKey: 'sidebar.recommendations.title',
    defaultTitle: 'Recommendations',
    defaultHeight: 2,
    minHeight: 1,
    maxHeight: 4,
    defaultCollapsed: true,
    order: 3,
    disabled: true, // Temporarily disabled - set to false to re-enable
  },
  drawSimulator: {
    id: 'drawSimulator',
    icon: '🎴',
    titleKey: 'sidebar.drawSimulator.title',
    defaultTitle: 'Draw Simulator',
    defaultHeight: 'auto',
    minHeight: 1,
    maxHeight: 12,
    defaultCollapsed: true,
    order: 4,
  },
  preloadDecks: {
    id: 'preloadDecks',
    icon: '📁',
    titleKey: 'sidebar.sampleDecks.title',
    defaultTitle: 'Sample Decks',
    defaultHeight: 'auto',
    minHeight: 1,
    maxHeight: 12,
    defaultCollapsed: true,
    order: 5,
    fillRemaining: true,
    disabled: true, // Temporarily disabled - set to false to re-enable
  },
};

// Get ordered array of tile configs
export const getOrderedTiles = () => {
  return Object.values(TILE_CONFIG).sort((a, b) => a.order - b.order);
};

// Calculate grid template columns based on layout mode
export const getGridTemplateColumns = () => {
  return `repeat(${GRID_CONFIG.totalColumns}, 1fr)`;
};

// Get column span for deck area based on layout mode
export const getDeckAreaSpan = (layoutMode = 'default') => {
  const layouts = GRID_CONFIG.layouts;
  return (layouts[layoutMode] || layouts.default).deckArea;
};

// Get column span for tile area based on layout mode
export const getTileAreaSpan = (layoutMode = 'default') => {
  const layouts = GRID_CONFIG.layouts;
  return (layouts[layoutMode] || layouts.default).tileArea;
};

export default TILE_CONFIG;
